import { createSelector } from '@ngrx/store';
import { BuyingPowerDetails } from '@zero/shared/util-models';
import { MultiOrderInstrument } from '../entities/multi-order-instrument';
import { tradeFeature } from './trade.reducers';

const getSum = (instruments: MultiOrderInstrument[]) =>
  instruments.reduce(
    (acc, { estimatedSum, direction, minimumSurcharge }) =>
      acc + estimatedSum + (direction === 'buy' ? (minimumSurcharge ?? 0) : -(minimumSurcharge ?? 0)),
    0,
  );
const hasEnoughBuyingPower = (buySum: number, bpDetails: BuyingPowerDetails) => Boolean(buySum <= bpDetails?.buyingPower);
const hasNoInstrumentErrors = (instruments: MultiOrderInstrument[]) => instruments.every(({ error }) => !error);
const hasNoInstrumentNotices = (instruments: MultiOrderInstrument[]) => instruments.every(({ notice }) => !notice);
const hasQuantity = (instruments: MultiOrderInstrument[]) => instruments.every(({ qty }) => qty);

export const selectMultiOrderBuyList = createSelector(tradeFeature.selectMultiOrderInstruments, (instruments) =>
  instruments.filter(({ direction }) => direction === 'buy'),
);

export const selectMultiOrderSellList = createSelector(tradeFeature.selectMultiOrderInstruments, (instruments) =>
  instruments.filter(({ direction }) => direction === 'sell'),
);

export const selectEstimatedBuySum = createSelector(selectMultiOrderBuyList, getSum);
export const selectEstimatedSellSum = createSelector(selectMultiOrderSellList, getSum);
export const selectEstimatedSum = createSelector(
  selectEstimatedBuySum,
  selectEstimatedSellSum,
  (buySum: number, sellSum: number) => ((buySum ?? 0) - (sellSum ?? 0)) * -1,
);
export const selectShouldDisplaySum = createSelector(
  selectEstimatedBuySum,
  selectEstimatedSellSum,
  tradeFeature.selectMultiOrderInstruments,
  (buySum: number, sellSum: number, instruments) => Boolean(buySum && sellSum) && hasNoInstrumentErrors(instruments),
);
export const selectShouldDisplayEstimatedSellSum = createSelector(
  selectEstimatedBuySum,
  selectEstimatedSellSum,
  (buySum: number, sellSum: number) => Boolean(!buySum && sellSum),
);
export const selectShouldDisplayEstimatedBuySum = createSelector(
  selectEstimatedBuySum,
  selectEstimatedSellSum,
  (buySum: number, sellSum: number) => Boolean(buySum && !sellSum),
);

export const selectHasNotEnoughBuyingPower = createSelector(
  selectEstimatedBuySum,
  tradeFeature.selectBuyingPower,
  (buySum: number, bpDetails: BuyingPowerDetails) => !hasEnoughBuyingPower(buySum, bpDetails),
);

export const selectValidForm = createSelector(
  tradeFeature.selectMultiOrderInstruments,
  selectEstimatedBuySum,
  tradeFeature.selectBuyingPower,
  (instruments: MultiOrderInstrument[], buySum: number, bpDetails: BuyingPowerDetails) =>
    Boolean(
      bpDetails &&
        instruments.length &&
        hasNoInstrumentErrors(instruments) &&
        hasNoInstrumentNotices(instruments) &&
        hasQuantity(instruments) &&
        hasEnoughBuyingPower(buySum, bpDetails),
    ),
);

export const selectMultiOrderErrors = createSelector(
  tradeFeature.selectMultiOrderInstruments,
  selectHasNotEnoughBuyingPower,
  tradeFeature.selectMultiOrderValidationError,
  (instruments, hasNotEnoughBuyingPower, validationError) => {
    const errors = [];
    if (instruments.length && instruments.every(({ quote }) => quote) && hasNotEnoughBuyingPower) {
      errors.push('trade.multi-order.create.buy.error.buying-power');
    }
    if (validationError) {
      errors.push(validationError);
    }
    return errors;
  },
);

export const selectCombinedFractionalOrder = createSelector(tradeFeature.selectCombinedOrder, (orders) =>
  orders?.find(({ execType }) => execType === 'fraction'),
);

export const selectCombinedMarketOrder = createSelector(tradeFeature.selectCombinedOrder, (orders) =>
  orders?.find(({ execType }) => execType === 'market'),
);

export const selectIsFractionOrderOnly = createSelector(
  selectCombinedFractionalOrder,
  selectCombinedMarketOrder,
  (fractionalOrder, marketOrder) => fractionalOrder && !marketOrder,
);
